import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../../components/button/Button';
import { BUTTON_TYPE } from '../../../components/button/constants';
import { HEADING } from '../../../components/heading/constants/constants';
import Heading from '../../../components/heading/Heading';
import HorizontalSeparator from '../../../components/separator/horizontalSeparator/HorizontalSeparator';
import { VEC_ICON_NAME } from '../../../components/icons/constants';
import PreLoginLayout from '../components/preloginlayout/PreLoginLayout';
import styles from './SignUp.styles';
import { componentKey, resetSignUpPage, setSignUpFormErrors, setSignUpFormFields } from './SignUpSlice';
import { componentKey as authenticationComponentKey } from '../../authentication/AuthenticationSlice';
import { useForm, Controller } from "react-hook-form";
import { signUpUser, signUpWThirdParty, validateEmailExists } from './SignUpSaga';
import CheckboxWLabel from '../../../components/checkbox/checkboxwlabel/CheckboxWLabel';
import Loadable from '../../../components/loadable/Loadable';
import TextInput from '../../../components/input/textinput/TextInput';
import PasswordInput from '../../../components/input/passwordinput/PasswordInput';
import {  useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useCallback } from 'react';
import { PRE_LOGIN_LOADING_STATES, PRE_LOGIN_STATES_MESSAGE_MAP, SSO_TEXTS } from '../constants';
import { INTERNAL_USER_EMAIL_DOMAINS, PERFTEST_EMAIL_DOMAIN, VALIDATE_NAME_REGEX } from '../../../../libs/utility/constants';
import useTxNavigate from '../../../../libs/txnavigate/TxNavigateHook';
import General from '../../../../libs/utility/General';
import HoverContainer from '../../../components/hovercontainer/HoverContainer';
import { GoogleLogin } from '@react-oauth/google';
import AppleLogin from 'react-apple-login';
import Config from '../../../config.web';
import { FEATURE_FLAG } from '../../../featureFlags';
import useGarbageCollector from '../../../../libs/garbagecollector/garbageCollectorHooks';
import { TERMS_AND_CONDITIONS_LINK } from '../../constants/constants';

export default function SignUp() {
    
    useGarbageCollector(componentKey);

    const { signUpFormFields, signUpFormErrors, emailCanBeUsed, loadingState, signedUpUserData } = useSelector((state) => state[componentKey]);
    const { isUserAuthenticated } = useSelector((state) => state[authenticationComponentKey]);
    const dispatch = useDispatch();
    const navigate = useTxNavigate();
    const { register, control, handleSubmit, watch, formState: {errors}, reset } = useForm();

    
    const { executeRecaptcha } = useGoogleReCaptcha();


    const handleReCaptchaVerify = useCallback(async () => {
        if (executeRecaptcha) {
            return await executeRecaptcha('sign_up');
        }
        
    }, [executeRecaptcha]);

    useEffect(() => {
        handleReCaptchaVerify();
    }, [handleReCaptchaVerify]);

    useEffect(()=>{
        if(!signedUpUserData?.EncUID) {
            return
        }

        const userEncUID = signedUpUserData?.EncUID
        const userDomain = General.getUserDomain(signedUpUserData.Email)

        switch (userDomain) {
            case PERFTEST_EMAIL_DOMAIN:
                return
            case INTERNAL_USER_EMAIL_DOMAINS.ZENWORK:
            case INTERNAL_USER_EMAIL_DOMAINS.TAX1099:
                window.userGuiding.identify(userEncUID, { newuser: 'true', internaluser: "true" })
                break
        
            default:
                window.userGuiding.identify(userEncUID, { newuser : 'true', internaluser: "false" })
                break;
        }         
    },[signedUpUserData?.EncUID])


    useEffect(() => {
        if(isUserAuthenticated){
            navigate('/')
        } else if (!isUserAuthenticated && !FEATURE_FLAG.ALLOW_USER_SIGN_UP) {
            navigate("/signin");
        }
    }, [isUserAuthenticated]);

    useEffect(() => {
        return () => {
            dispatch(resetSignUpPage());
        };
    }, []);

    const password = watch('password');
    const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*]).{8,}$/;

    const validateConfirmPassword = (value) => {
        if (value !== password) {
        return "Passwords don't match";
        }
    }

    const validateTermsChecked = (value) => {
        if(!signUpFormFields.agreeTerms) {
            return "Please accept the terms & conditions"
        }
    }

    const handleInputChange = (e, onChangeValidationController, fieldType) => {
        
        const { name, value } = e.target;
        onChangeValidationController(value);
        let formFields = {};
        if(e.target.type === 'checkbox'){
            formFields = {
                ...signUpFormFields,
                [name]: (e.target.checked),
            }
        }else{
            formFields = {
                ...signUpFormFields,
                [name]: value,
            }
        }
        dispatch(setSignUpFormFields(formFields));
    };

    const handleEmailOnBlur = (e) => {
        const { name, value } = e.target;
        if(value === ""){
            dispatch(setSignUpFormErrors({
                emailErrorStatus: true,
                emailErrorMessage: "Email cannot be empty",
            }));
        }else{
            var validEmailRegex = /\S+@\S+\.\S+/;
            if (value.match(validEmailRegex)) {
                handleReCaptchaVerify().then(gToken =>
                    {
                        const token = General.handleGRecaptchaToken(gToken)
                        if(token){
                            dispatch(validateEmailExists({email: value, GRecaptchaResponse: token}))
                        }
                    }
                )
            } else {        
                dispatch(setSignUpFormErrors({
                    emailErrorStatus: true,
                    emailErrorMessage: "Email format is incorrect",
                }));
            }
        }
    };

    const handleFormSubmit = (data) => {
        if(!emailCanBeUsed){
            dispatch(setSignUpFormErrors({
                emailErrorStatus: true,
                emailErrorMessage: "Email is required",
            }));
        }else{
            handleReCaptchaVerify().then(gToken => 
                {
                    const token = General.handleGRecaptchaToken(gToken)
                    dispatch(signUpUser({...signUpFormFields, GRecaptchaResponse: token}))
                }
            )
        }
    };

    const onGoogleSignup = (response) =>{
        dispatch(signUpWThirdParty({
            IDToken: response.credential,
            Email:"",
            SSOType: SSO_TEXTS.GOOGLE_TEXT,
            flag: SSO_TEXTS.SIGNUP_TEXT
        }));
    }

    const onAppleSignup = (response) => {
        if(response?.authorization?.id_token){
            dispatch(signUpWThirdParty({
                IDToken: response.authorization.id_token,
                Email: "",
                SSOType: SSO_TEXTS.APPLE_TEXT,
                flag: SSO_TEXTS.SIGNUP_TEXT
            }));
        }
    };

    return (
        <PreLoginLayout>
            <Loadable
                currentState={loadingState.state}
                loadingStates={[PRE_LOGIN_LOADING_STATES.SIGN_UP_PAGE_LOADING, PRE_LOGIN_LOADING_STATES.SSO_SIGN_UP_PAGE_LOADING]}
                messageMap={PRE_LOGIN_STATES_MESSAGE_MAP}
                isNewLoadable={true}
            >
                <div style={styles.loginForm}>
                    <Heading type={HEADING.H1}>Sign Up</Heading>
                    <form onSubmit={handleSubmit(handleFormSubmit)}>

                        <div style={{...styles.fullNameContainer, ...styles.fieldMarginTop}}>
                            <div style={styles.nameFieldsDiv}>
                                <Controller
                                    control={control}
                                    name="firstName"
                                    defaultValue={signUpFormFields.firstName}
                                    rules={{ 
                                        required: "First name cannot be empty",
                                        regex: { pattern: VALIDATE_NAME_REGEX, message: "Should only consist any of A-Z,a-z, 0-9,-,.&' letters and maximum 40 character" }
                                    }}
                                    render={({ field : {value, onChange} }) => (
                                        <TextInput 
                                            type={'text'}
                                            placeHolder={'First name'} 
                                            prefixProps={{ icon: VEC_ICON_NAME.USER_PROFILE }} 
                                            name={'firstName'} 
                                            value={value} 
                                            onChangeCb={(e) => handleInputChange(e, onChange)} 
                                        />
                                    )}
                                />
                                {errors.firstName && <span style={styles.errorMessage}>{errors.firstName.message}</span>}
                            </div>
                            <div style={styles.nameFieldsDiv}>
                                <Controller
                                    control={control}
                                    name="lastName"
                                    defaultValue={signUpFormFields.lastName}
                                    rules={{ 
                                        required: "Last name cannot be empty",
                                        regex: { pattern: VALIDATE_NAME_REGEX, message: "Should only consist any of A-Z,a-z, 0-9,-,.&' letters and maximum 40 character" }
                                    }}
                                    render={({ field : {value, onChange} }) => (
                                        <TextInput 
                                            type={'text'}
                                            placeHolder={'Last name'}
                                            name={'lastName'} 
                                            value={value} 
                                            onChangeCb={(e) => handleInputChange(e, onChange)} 
                                        />
                                    )}
                                />
                                {errors.lastName && <span style={styles.errorMessage}>{errors.lastName.message}</span>}
                            </div>
                        </div>
                        <div style={styles.fieldMarginTop}>
                            <TextInput 
                                id={'email'} 
                                type={'text'} 
                                placeHolder={'Email'} 
                                prefixProps={{ icon: VEC_ICON_NAME.EMAIL_ICON }} 
                                name={'email'} 
                                value={signUpFormFields.email} 
                                onChangeCb={(e) => handleInputChange(e, () => {})} 
                                onBlurCb={(e) => handleEmailOnBlur(e)} 
                            />
                            {signUpFormErrors.emailErrorStatus && <span style={styles.errorMessage}>{signUpFormErrors.emailErrorMessage}</span>}
                        </div>

                        <div style={styles.fieldMarginTop}>
                            <Controller
                                control={control}
                                name="password"
                                defaultValue={signUpFormFields.password}
                                rules={{ 
                                    required: "Password cannot be empty",
                                    pattern: {
                                        value: passwordRegex,
                                        message: "Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one number and one special character"
                                    }
                                }}
                                render={({ field : {value, onChange} }) => (
                                    <PasswordInput 
                                        name="password" 
                                        type="password" 
                                        placeHolder='Password' 
                                        value={value} 
                                        onChangeCb={(e) => handleInputChange(e, onChange, "password")} 
                                    />
                                )}
                            />
                            {errors.password && <span style={styles.errorMessage}>{errors.password.message}</span>}
                            <div style={styles.marginTopDependentField}>                        
                                <Controller
                                    control={control}
                                    name="confirmPassword"
                                    defaultValue={signUpFormFields.confirmPassword}
                                    rules={{ 
                                        required: "Confirm password cannot be empty",
                                        validate: validateConfirmPassword,
                                    }}
                                    render={({ field : {value, onChange} }) => (
                                        <PasswordInput 
                                            name="confirmPassword" 
                                            type="password" 
                                            placeHolder='Confirm password' 
                                            value={value} 
                                            onChangeCb={(e) => handleInputChange(e, onChange, "password")} 
                                        />
                                    )}
                                />
                                {errors.confirmPassword && <span style={styles.errorMessage}>{errors.confirmPassword.message}</span>}
                            </div>
                        </div>
                        <div style={styles.fieldMarginTop}>
                            <CheckboxWLabel name={"enableAPI"} label={"Enable API Access"} checked={signUpFormFields.enableAPI} onChangeCb={(e) => handleInputChange(e, () => {}, "checkbox")} />
                            <div style={styles.agreeTermsDiv}>
                                <Controller
                                    control={control}
                                    name="agreeTerms"
                                    defaultValue={signUpFormFields.agreeTerms}
                                    rules={{ 
                                        validate: validateTermsChecked,
                                    }}
                                    render={({ field: {value, onChange}  }) => (
                                        <CheckboxWLabel 
                                            name={"agreeTerms"} 
                                            label={"I Accept"}
                                            checked={signUpFormFields.agreeTerms} 
                                            onChangeCb={(e) => handleInputChange(e, onChange, "checkbox")} 
                                        />
                                    )}
                                />
                                <div style={styles.agreeTermsLink}><a target="_blank" href={TERMS_AND_CONDITIONS_LINK}>Terms & Conditions</a></div>
                            </div>
                            {errors.agreeTerms && <span style={styles.errorMessage}>{errors.agreeTerms.message}</span>}
                            <div style={styles.marketingCheckboxDiv}>
                                <CheckboxWLabel name={"isReceiveCompliance"} label={"Receive Compliance Updates and Marketing Communications."} checked={signUpFormFields.isReceiveCompliance} onChangeCb={(e) => handleInputChange(e, () => {}, "checkbox")} />
                            </div>
                        </div>
                        <div style={styles.submitButtonDiv}>
                            <Button type={BUTTON_TYPE.PRIMARY}>
                                Create Account
                            </Button>
                        </div>
                    </form>
                    {FEATURE_FLAG.USER_SSO_LOGIN ? <div style={styles.signWithOptionsMainContainer}>
                        <p style={styles.signInOptionsTitle}>Sign up with any of these options</p>
                        <div style={styles.signUpOptionsContainer}>
                            <HoverContainer>
                                <GoogleLogin
                                    shape='circle'
                                    context="signup"
                                    size='large'
                                    onSuccess={onGoogleSignup}
                                    onError={(err) => {
                                        console.log('errror', err)
                                    }}
                                    type="icon"
                                />
                            </HoverContainer>
                            {Config.APPLE_LOGIN_CLIENT_ID && Config.APP_BASE_URL ? <HoverContainer>
                                <AppleLogin
                                    clientId={Config.APPLE_LOGIN_CLIENT_ID}
                                    redirectURI={Config.APP_BASE_URL + "/"}
                                    usePopup={true}
                                    callback={onAppleSignup}
                                    scope="email name"
                                    responseMode="query"
                                    render={renderProps => (
                                        <Button prefixProps={{ icon: VEC_ICON_NAME.APPLE_LOGIN_ICON }} style={styles.appleSignupButton} type={BUTTON_TYPE.PRIMARY_LIGHT_WITH_BORDER} onClickCb={renderProps.onClick} />
                                    )}
                                />
                            </HoverContainer> : null}
                        </div>
                    </div> : null}
                    <div style={styles.separatorDiv}><HorizontalSeparator /></div>
                    <div style={styles.bottomLinkContainer}>
                        <span>If you already have an account</span>
                        <div>
                            <Button type={BUTTON_TYPE.LIGHT_WITH_NO_BORDER} style={styles.signUpLink} onClickCb={() => {navigate('/signin')}}>
                                Sign In
                            </Button>
                        </div>
                    </div>
                </div>
            </Loadable>
        </PreLoginLayout>
    )
}